<!--
  @component ActivityFeedBlock — the live account-wide event feed.
  tier:      home block (kind: "activity-feed")
  purpose:   the "scores ticker" — recent deploys/builds across every project,
             newest first, friendly labels, tone-colored dots.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { feedEntries, relTime } from "../home-util.js";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();

  const entries = $derived(feedEntries(provenance.events, 14));

  function projName(pid: string | null): string {
    if (!pid) return "platform";
    return identity.projects.find((p) => p.id === pid)?.name ?? pid;
  }
</script>

<section class="block">
  <header class="bh">
    <h2>Activity</h2>
    <span class="live"><span class="led"></span>Live</span>
  </header>

  {#if entries.length === 0}
    <div class="empty">No activity yet — deploy a project and it streams here live.</div>
  {:else}
    <ul class="feed">
      {#each entries as e (e.id)}
        <li>
          <span class="dot {e.tone}" aria-hidden="true"></span>
          <span class="lbl">{e.label}</span>
          <span class="proj">{projName(e.pid)}</span>
          <span class="time">{relTime(e.atMs)}</span>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .block {
    display: flex; flex-direction: column; gap: var(--space-3);
    background: var(--glass-fill); border: 0.5px solid var(--border);
    border-radius: var(--radius-lg, 14px); padding: var(--space-5); font-family: var(--type-font);
    height: 100%;
  }
  .bh { display: flex; align-items: center; justify-content: space-between; }
  .bh h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .live { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; color: var(--text-tertiary); }
  .live .led { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--success); animation: pulse 1.8s ease-in-out infinite; }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

  .empty { font-size: 13px; color: var(--text-tertiary); line-height: 1.6; padding: var(--space-4) 0; }

  .feed { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
  .feed li {
    display: flex; align-items: center; gap: var(--space-3);
    padding: 9px 0; border-top: 0.5px solid var(--border-subtle);
  }
  .feed li:first-child { border-top: 0; }
  .dot { width: 7px; height: 7px; border-radius: var(--radius-pill); flex-shrink: 0; background: var(--text-tertiary); }
  .dot.ok { background: var(--success); }
  .dot.error { background: var(--error); }
  .dot.warn { background: var(--warning, #e6b450); }
  .dot.info { background: var(--accent, #6ea8fe); }
  .lbl { font-size: 13px; color: var(--text-secondary); white-space: nowrap; }
  .proj {
    flex: 1; min-width: 0; font-size: 12px; color: var(--text-tertiary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: right;
  }
  .time { font-size: 11px; color: var(--text-ghost); flex-shrink: 0; font-variant-numeric: tabular-nums; }
</style>
